import React from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { StaffingRatioChart } from '../components/charts/StaffingRatioChart';
interface StaffingRatioPageProps {
  onBackToOverview: () => void;
}
export const StaffingRatioPage: React.FC<StaffingRatioPageProps> = ({
  onBackToOverview
}) => {
  // Sample data for facilities and their staffing ratios
  const facilities = [{
    id: 1,
    name: 'Lakeside Gardens',
    location: 'Portland, OR',
    residents: 124,
    staff: 42,
    ratio: 2.95,
    careHoursPerResident: 3.8,
    changeFromLastQuarter: '+0.3'
  }, {
    id: 2,
    name: 'Sunset Meadows',
    location: 'Seattle, WA',
    residents: 98,
    staff: 35,
    ratio: 2.8,
    careHoursPerResident: 3.5,
    changeFromLastQuarter: '-0.2'
  }, {
    id: 3,
    name: 'Harbor View',
    location: 'San Francisco, CA',
    residents: 156,
    staff: 62,
    ratio: 2.52,
    careHoursPerResident: 4.2,
    changeFromLastQuarter: '+0.5'
  }, {
    id: 4,
    name: 'Mountain Terrace',
    location: 'Denver, CO',
    residents: 112,
    staff: 38,
    ratio: 2.95,
    careHoursPerResident: 3.6,
    changeFromLastQuarter: '0.0'
  }, {
    id: 5,
    name: 'Parkside Manor',
    location: 'Chicago, IL',
    residents: 143,
    staff: 45,
    ratio: 3.18,
    careHoursPerResident: 3.2,
    changeFromLastQuarter: '-0.3'
  }];
  return <main className="flex-1 overflow-y-auto bg-gray-50 p-6" data-id="element-656">
      <div className="mb-6" data-id="element-657">
        <div className="flex items-center justify-between mb-4" data-id="element-658">
          <h1 className="text-2xl font-bold" data-id="element-659">
            Staff-to-Resident Ratio Comparison
          </h1>
          <button onClick={onBackToOverview} className="flex items-center px-4 py-2 bg-[#1f58b0] text-white rounded-md hover:bg-[#255db7] transition-colors" data-id="element-660">
            <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-661" />
            Back to Overview
          </button>
        </div>
        <p className="text-gray-600 mb-4" data-id="element-662">
          This report compares staff-to-resident ratios across all Further
          managed facilities, highlighting staffing efficiency and care quality
          indicators.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-663">
          <div className="bg-white p-4 rounded-lg shadow" data-id="element-664">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-665">
              Average Staff-to-Resident Ratio
            </h3>
            <p className="text-2xl font-bold text-[#1f58b0]" data-id="element-666">1:2.88</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow" data-id="element-667">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-668">
              Best Performing Facility
            </h3>
            <p className="text-2xl font-bold text-[#255db7]" data-id="element-669">Harbor View</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow" data-id="element-670">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-671">
              Average Care Hours Per Resident
            </h3>
            <p className="text-2xl font-bold text-[#f6d052]" data-id="element-672">3.7</p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow p-4 mb-6" data-id="element-673">
        <h2 className="text-lg font-semibold mb-4" data-id="element-674">
          Staffing Ratio Comparison
        </h2>
        <div className="h-64" data-id="element-675">
          <StaffingRatioChart data-id="element-676" />
        </div>
      </div>
      <div className="bg-white rounded-lg shadow" data-id="element-677">
        <div className="p-4 border-b" data-id="element-678">
          <h2 className="text-lg font-semibold" data-id="element-679">
            Detailed Facility Comparison
          </h2>
          <p className="text-sm text-gray-500" data-id="element-680">
            Sorted by staff-to-resident ratio (lower is better)
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-681">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-682">
            <thead className="bg-gray-50" data-id="element-683">
              <tr data-id="element-684">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-685">
                  Facility
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-686">
                  Location
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-687">
                  Residents
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-688">
                  Staff
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-689">
                  Ratio (Res:Staff)
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-690">
                  Care Hours/Resident
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-691">
                  Change (QoQ)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-692">
              {facilities.sort((a, b) => a.ratio - b.ratio).map(facility => <tr key={facility.id} className="hover:bg-gray-50" data-id="element-693">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-694">
                      <div className="text-sm font-medium text-gray-900" data-id="element-695">
                        {facility.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-696">
                      {facility.location}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-697">
                      {facility.residents}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-698">
                      {facility.staff}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-699">
                      1:{facility.ratio}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-700">
                      {facility.careHoursPerResident} hrs
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-701">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${facility.changeFromLastQuarter.startsWith('+') ? 'bg-green-100 text-green-800' : facility.changeFromLastQuarter.startsWith('-') ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-702">
                        {facility.changeFromLastQuarter}
                      </span>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </main>;
};